import React from 'react';
import {
  Icon,
  // type IconName,
  Button,
  cn,
  Loader,
} from '@tasama/ui';
import { useTranslation, i18n } from '@tasama/i18n';
import type { BackendNotificationData } from '../features/Notifications/types/notification-types';
import { formatNotificationTime } from '../features/Notifications/utils/notifications-utils';
import { useServiceTitlesListQuery } from '@/hooks/useServiceTitlesListQuery';

export interface NotificationFloatingItemProps {
  /** Notification data from backend */
  notification: BackendNotificationData;
  /** Resolved (translated + interpolated) title string */
  resolvedTitle: string;
  /** Resolved (translated + interpolated) message string */
  resolvedMessage: string;
  /** Whether the notification is unread */
  isUnread?: boolean;
  /** Click handler for the notification */
  onClick?: () => void;
  /** Test ID for QA automation */
  testID?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * NotificationFloatingItem Component
 * Based on Figma design: https://www.figma.com/design/IcSV9qhrjLJh6Y3Q8F7lcr/Tasama-UI-UX-Design---Phase-1?node-id=7603-104107&m=dev
 *
 * A compact notification item designed for floating menus
 * Features:
 * - Icon with brand green background
 * - Title and message display
 * - Timestamp
 * - Unread indicator dot
 * - Hover states
 * - Click interaction
 */
export const NotificationFloatingItem: React.FC<
  NotificationFloatingItemProps
> = ({
  notification,
  resolvedTitle,
  resolvedMessage,
  isUnread = false,
  onClick,
  testID = 'notification-floating-item',
  className,
}) => {
  const { t } = useTranslation('portal');
  const time = formatNotificationTime(notification.createdAt);

  const getPrimaryAction = (
    actions?: Array<{
      label: string;
      url: string;
      actionType: string;
      displayOrder: number;
    }> | null
  ) => {
    if (!actions || actions.length === 0) return null;
    const view = actions.find((a) => a.actionType?.toUpperCase() === 'VIEW');
    return view || actions[0];
  };

  const primaryAction = getPrimaryAction(notification.actions);

  return (
    <div
      className={cn(
        'flex items-start gap-3',
        'border rounded-xl border-brand-tint-300',
        'px-xl py-lg',
        'm-2',
        'cursor-pointer',
        'transition-colors duration-200',
        'hover:bg-bg-secondary',
        'hover:shadow-[0_4px_6px_-1px_var(--color-shadow-md),0_2px_4px_-2px_var(--color-shadow-sm)]',
        className
      )}
      onClick={onClick}
      data-testid={testID}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {/* Unread Indicator */}
      {isUnread && (
        <div
          className="w-2 h-2 rounded-full bg-brand-blue-500 flex-shrink-0 mt-xs"
          data-testid={`${testID}-unread-dot`}
          aria-label={t('notifications.unreadAriaLabel')}
        />
      )}

      {/* Content Container */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-xs">
          <span
            className={cn(
              'text-sm font-medium text-gray-900 truncate',
              isUnread && 'font-semibold'
            )}
            data-testid={`${testID}-title`}
          >
            {resolvedTitle ||
              notification.notificationCategory ||
              t('common:notification.defaultTitle')}
          </span>
          {time && (
            <span
              className="text-xs text-gray-500 flex-shrink-0"
              data-testid={`${testID}-time`}
            >
              {time}
            </span>
          )}
        </div>
        {/* Message (85%) + CTA (15%) in 2 columns, same row, below the title row */}
        {resolvedMessage && (
          <div className="flex flex-row items-center gap-2 w-full mt-1">
            <div className="flex-1 min-w-0">
              <p
                className="text-xs text-gray-600 line-clamp-1"
                data-testid={`${testID}-message`}
              >
                {resolvedMessage}
              </p>
            </div>
            {primaryAction && (
              <div className="flex-shrink-0 flex items-center justify-end">
                <Button
                  type="button"
                  variant="quaternary"
                  size="sm"
                  className="text-fg-brand-primary hover:text-fg-brand-primary shrink-0 p-0 min-w-[40px] ms-1"
                  testID={`${testID}-view-button`}
                  label={i18n.t(primaryAction.label).toUpperCase()}
                  iconPosition="none"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

NotificationFloatingItem.displayName = 'NotificationFloatingItem';

export interface NotificationFloatingViewProps {
  /** Array of notifications to display */
  notifications: BackendNotificationData[];
  /** Maximum number of notifications to show */
  maxItems?: number;
  /** Handler for notification click */
  onNotificationClick?:
    | ((_notification: BackendNotificationData) => void)
    | undefined;
  /** Handler for "View All" click */
  onViewAllClick?: (() => void) | undefined;
  /** Test ID for QA automation */
  testID?: string | undefined;
  /** Additional CSS classes */
  className?: string | undefined;
  /** Loading state while notifications are being fetched */
  isLoading?: boolean;
}

/**
 * NotificationFloatingView Component
 * Based on Figma design: https://www.figma.com/design/IcSV9qhrjLJh6Y3Q8F7lcr/Tasama-UI-UX-Design---Phase-1?node-id=7603-104107&m=dev
 *
 * A complete notification view for floating menus
 * Features:
 * - List of notification items
 * - Empty state
 * - "View All" footer
 * - Proper scrolling for long lists
 */
export const NotificationFloatingView: React.FC<
  NotificationFloatingViewProps
> = ({
  notifications,
  onNotificationClick,
  onViewAllClick,
  testID = 'notification-floating-view',
  className,
  isLoading = false,
}) => {
  const { t } = useTranslation('portal');
  const { serviceTitles, loading: serviceTitlesLoading } =
    useServiceTitlesListQuery();
  const hasNotifications = notifications.length > 0;

  const resolveNotification = (notification: BackendNotificationData) => {
    const args: Record<string, string> = { ...notification.messageArgs };
    if (args.serviceName) {
      args.serviceName = serviceTitles?.[args.serviceName] ?? args.serviceName;
    }
    if (args.stepName) {
      args.stepName = serviceTitles?.[args.stepName] ?? args.stepName;
    }
    if (args.statusKey) {
      args.status = i18n.t(`business-landing:${args.statusKey}`);
    }
    return {
      resolvedTitle: i18n.t(`portal:${notification.titleKey}`, args),
      resolvedMessage: i18n.t(`portal:${notification.messageKey}`, args),
    };
  };

  return (
    <div
      className={cn('flex flex-col p-2 h-full', className)}
      data-testid={testID}
    >
      {/* Header with Title and View All */}
      <div
        className={cn(
          'flex items-center justify-between',
          'px-4 py-3',
          'border-gray-200'
        )}
        data-testid={`${testID}-header`}
      >
        <h3 className="text-lg font-normal text-gray-900">
          {t('notifications.title')}
        </h3>
        {hasNotifications && onViewAllClick && (
          <Button
            type="button"
            variant="quaternary"
            labelClassName="uppercase"
            className={cn(
              'flex items-center gap-1',
              'text-xs font-semibold text-brand-blue-500',
              'cursor-pointer',
              'transition-colors duration-200',
              'hover:text-brand-blue-600',
              'focus:outline-none'
            )}
            onClick={onViewAllClick}
            data-testid={`${testID}-view-all`}
            aria-label={t('notifications.viewAllAriaLabel')}
            label={t('common:actions.viewAll')}
          />
        )}
      </div>

      {/* Loading State */}
      {isLoading || serviceTitlesLoading ? (
        <div className="flex items-center justify-center h-11xl ">
          <Loader
            size="md"
            showLabel={true}
            label={t('notifications.loading')}
            testID={`${testID}-loader`}
          />
        </div>
      ) : /* Notifications List */
      !hasNotifications ? (
        <div
          className="px-4 py-8 text-center"
          data-testid={`${testID}-empty-state`}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
              <Icon
                name="Bell"
                size={24}
                className="text-gray-400"
                testID={`${testID}-empty-icon`}
              />
            </div>
            <p className="text-sm text-gray-500">{t('notifications.empty')}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col" data-testid={`${testID}-list`}>
          {notifications.map((notification) => {
            const isUnread = notification.status?.toLowerCase() !== 'read';
            const { resolvedTitle, resolvedMessage } =
              resolveNotification(notification);
            return (
              <NotificationFloatingItem
                key={notification.notificationId}
                notification={notification}
                resolvedTitle={resolvedTitle}
                resolvedMessage={resolvedMessage}
                isUnread={isUnread}
                onClick={() => onNotificationClick?.(notification)}
                testID={`${testID}-item-${notification.notificationId}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

NotificationFloatingView.displayName = 'NotificationFloatingView';
